import { useEffect, useRef, useState } from 'react';
import { druckenWennBereit } from '@/lib/druckBereit';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, ApiError } from '@/lib/api';
import { Spinner } from '@/components/ui/Spinner';
import { ZettelBlatt, type ZettelDaten } from '@/components/druck/ZettelBlatt';
import {
  LIEFERSCHEIN_VARIANTEN,
  LieferscheinBlatt,
  lieferscheinFehlerText
} from '@/components/druck/LieferscheinBlatt';
import type { LieferscheinAntwort, LieferscheinVariante } from '@/types/lieferschein';

// Druckseite für Safari, geöffnet aus der iPhone-Home-Bildschirm-App
// (lib/iosDruck.ts, Migration 036).
//
// OHNE SITZUNG: Safari hat die Anmeldung der App nicht. Berechtigt ist,
// wer das Zeichen in der Adresse hat — 10 Minuten, eine Bestellung, nur
// Lesen. __root.tsx lässt diese Route deshalb ohne Anmeldung durch.
//
// Gedruckt wird dasselbe Blatt wie auf den Druckseiten mit Sitzung
// (components/druck/), mit denselben Druckwurzeln aus index.css.

type Suche = { variante?: LieferscheinVariante };

type Antwort =
  | { art: 'zettel'; zettel: ZettelDaten }
  | { art: 'lieferschein'; lieferschein: LieferscheinAntwort };

function fehlerText(err: unknown): string {
  if (err instanceof ApiError && err.code === 'zeichen_abgelaufen') {
    return 'Dieser Druck-Link ist abgelaufen — er gilt 10 Minuten. Bitte in der App noch einmal auf Drucken tippen.';
  }
  return lieferscheinFehlerText(err);
}

function DruckenMitZeichen() {
  const { zeichen } = Route.useParams();
  const { variante } = Route.useSearch();
  const navigate = useNavigate();
  const gedruckt = useRef(false);

  const q = useQuery({
    queryKey: ['drucken', zeichen, variante ?? null],
    queryFn: () =>
      apiFetch<Antwort>(
        `/druck/einloesen.php?z=${encodeURIComponent(zeichen)}` +
          (variante ? `&variante=${encodeURIComponent(variante)}` : '')
      ),
    retry: false,
    staleTime: Infinity
  });

  const [massstab, setMassstab] = useState(1);
  useEffect(() => {
    function messen(): void {
      setMassstab(Math.min(1, (window.innerWidth - 32) / 718));
    }
    messen();
    window.addEventListener('resize', messen);
    return () => window.removeEventListener('resize', messen);
  }, []);

  // Einmal von selbst drucken — dafür ist man hier. Ein Wechsel der
  // Vorlage druckt nicht erneut.
  const geladen = q.data !== undefined;
  useEffect(() => {
    if (!geladen || gedruckt.current) return;
    gedruckt.current = true;
    // Erst wenn QR-Code und Cover geladen sind (lib/druckBereit.ts).
    return druckenWennBereit('.druck-wurzel, .lieferschein-wurzel', 400);
  }, [geladen]);

  const knoepfe = (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => window.print()}
        disabled={!geladen}
        className="min-h-[44px] rounded-md bg-akzent px-4 text-sm text-cream disabled:opacity-50"
      >
        Drucken
      </button>
      {q.data?.art === 'lieferschein' && (
        <label className="flex items-center gap-2 text-sm">
          <span>Vorlage</span>
          <select
            value={q.data.lieferschein.variante}
            onChange={(e) =>
              void navigate({
                to: '/drucken/$zeichen',
                params: { zeichen },
                search: { variante: e.target.value as LieferscheinVariante },
                replace: true
              })
            }
            className="min-h-[44px] rounded-md border border-line bg-white px-2 text-base"
          >
            {LIEFERSCHEIN_VARIANTEN.map((v) => (
              <option key={v} value={v}>
                {q.data?.art === 'lieferschein' ? q.data.lieferschein.varianten[v] : v}
              </option>
            ))}
          </select>
        </label>
      )}
      <p className="w-full text-xs text-ink-faint">
        Nach dem Drucken dieses Fenster schließen und zur App zurückwechseln.
      </p>
    </div>
  );

  if (q.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }
  if (q.isError) {
    return <p className="max-w-xl p-6 text-coral">{fehlerText(q.error)}</p>;
  }

  if (q.data.art === 'zettel') {
    return (
      <div className="druck-wurzel flex min-h-screen flex-col items-center bg-white p-6 text-black">
        <div className="nicht-drucken">{knoepfe}</div>
        <ZettelBlatt b={q.data.zettel} />
      </div>
    );
  }

  const d = q.data.lieferschein;
  const qrBild = `/api/qr.php?d=${encodeURIComponent(`${window.location.origin}/s/${d.kopf.nummer}`)}`;
  return (
    <div className="lieferschein-wurzel min-h-screen bg-white p-4 text-black">
      {knoepfe}
      <div className="lieferschein-rahmen" style={{ zoom: massstab }}>
        <LieferscheinBlatt daten={d} qrBild={qrBild} />
      </div>
    </div>
  );
}

export const Route = createFileRoute('/drucken/$zeichen')({
  validateSearch: (s: Record<string, unknown>): Suche => {
    const v = s.variante;
    return typeof v === 'string' && (LIEFERSCHEIN_VARIANTEN as string[]).includes(v)
      ? { variante: v as LieferscheinVariante }
      : {};
  },
  component: DruckenMitZeichen
});
